import React, { useState, useRef, useCallback, useMemo } from "react";
import API_BASE_URL from "./config";
import "./ShanBabe.css";

function formatSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
}

function isVideo(name) {
  return /\.(mp4|mov|avi|mkv|webm|m4v|wmv|flv|3gp)$/i.test(name);
}

export default function ShanBabe() {
  // Upload state
  const [uploadUnlocked, setUploadUnlocked] = useState(false);
  const [uploadPwd, setUploadPwd] = useState("");
  const [uploadPwdError, setUploadPwdError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMsg, setUploadMsg] = useState("");
  const fileInputRef = useRef(null);

  // Download / gallery state
  const [downloadUnlocked, setDownloadUnlocked] = useState(false);
  const [downloadPwd, setDownloadPwd] = useState("");
  const [downloadPwdError, setDownloadPwdError] = useState("");
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingKeys, setDownloadingKeys] = useState(new Set());

  // Preview
  const [previewFile, setPreviewFile] = useState(null);

  // Active tab
  const [tab, setTab] = useState("upload");

  // Stored validated passwords for API calls
  const [validUploadPwd, setValidUploadPwd] = useState("");
  const [validDownloadPwd, setValidDownloadPwd] = useState("");

  // Date grouping — which dates are expanded
  const [expandedDates, setExpandedDates] = useState(new Set());

  // Share link copied feedback
  const [copiedKey, setCopiedKey] = useState(null);

  // Upload password check — verify against server
  const unlockUpload = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/shan-anusha-babe/verify-upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: uploadPwd }),
      });
      if (res.ok) {
        setValidUploadPwd(uploadPwd);
        setUploadUnlocked(true);
        setUploadPwdError("");
      } else {
        setUploadPwdError("Wrong password");
      }
    } catch {
      setUploadPwdError("Server error");
    }
  };

  // Download password check & load files — verified by server
  const unlockDownload = async () => {
    setLoadingFiles(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/shan-anusha-babe/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: downloadPwd }),
      });
      if (res.status === 403) {
        setDownloadPwdError("Wrong password");
        setLoadingFiles(false);
        return;
      }
      const data = await res.json();
      if (res.ok) {
        setValidDownloadPwd(downloadPwd);
        setDownloadUnlocked(true);
        setDownloadPwdError("");
        setFiles(data.files || []);
      }
    } catch (err) {
      setDownloadPwdError("Server error");
    }
    setLoadingFiles(false);
  };

  const loadFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/shan-anusha-babe/list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: validDownloadPwd }),
      });
      const data = await res.json();
      if (res.ok) setFiles(data.files || []);
      else console.error(data.message);
    } catch (err) {
      console.error("Failed to load files:", err);
    }
    setLoadingFiles(false);
  }, [validDownloadPwd]);

  // File selection
  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // Upload with real-time progress via XMLHttpRequest
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadMsg("");

    const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    let bytesUploaded = 0;
    let filesUploaded = 0;

    const uploadSingleFile = (file) => {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append("password", validUploadPwd);
        formData.append("files", file);

        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_BASE_URL}/api/shan-anusha-babe/upload`);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const currentTotal = bytesUploaded + e.loaded;
            setUploadProgress(Math.round((currentTotal / totalSize) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            bytesUploaded += file.size;
            const data = JSON.parse(xhr.responseText);
            filesUploaded += (data.files || []).length;
            skippedCount += (data.skipped || []).length;
            resolve(data);
          } else {
            try {
              reject(new Error(JSON.parse(xhr.responseText).message || "Upload failed"));
            } catch {
              reject(new Error("Upload failed"));
            }
          }
        };

        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(formData);
      });
    };

    let skippedCount = 0;
    for (let i = 0; i < selectedFiles.length; i++) {
      try {
        await uploadSingleFile(selectedFiles[i]);
      } catch (err) {
        setUploadMsg(`Error on file ${i + 1}: ${err.message}`);
        setUploading(false);
        return;
      }
    }

    const msg = skippedCount > 0
      ? `Uploaded ${filesUploaded}, skipped ${skippedCount} (already exist)`
      : `Successfully uploaded ${filesUploaded} file(s)!`;
    setUploadMsg(msg);
    setSelectedFiles([]);
    setUploading(false);
    setUploadProgress(100);
  };

  // Download single file
  const downloadFile = async (file) => {
    setDownloadingKeys((prev) => new Set(prev).add(file.key));
    try {
      const res = await fetch(`${API_BASE_URL}/api/shan-anusha-babe/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: validDownloadPwd, key: file.key }),
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name.replace(/^\d+-/, "");
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Download failed");
    }
    setDownloadingKeys((prev) => {
      const s = new Set(prev);
      s.delete(file.key);
      return s;
    });
  };

  // Download all
  const downloadAll = async () => {
    setDownloadingAll(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/shan-anusha-babe/download-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: validDownloadPwd }),
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "shan-anusha-babe-media.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Download failed");
    }
    setDownloadingAll(false);
  };

  // Share link — copy presigned URL to clipboard
  const shareFile = async (file) => {
    try {
      await navigator.clipboard.writeText(file.url);
      setCopiedKey(file.key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = file.url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
      setCopiedKey(file.key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  const photos = files.filter((f) => !isVideo(f.name));
  const videos = files.filter((f) => isVideo(f.name));

  // Group files by date (newest first)
  const dateGroups = useMemo(() => {
    const groups = {};
    for (const f of files) {
      const d = f.lastModified ? new Date(f.lastModified) : new Date();
      const key = d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
      if (!groups[key]) groups[key] = { date: d, label: key, files: [] };
      groups[key].files.push(f);
    }
    return Object.values(groups).sort((a, b) => b.date - a.date);
  }, [files]);

  const toggleDate = (label) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <div className="sb-page">
      {/* Header */}
      <header className="sb-header">
        <div className="sb-header-inner">
          <h1 className="sb-title">
            <span className="sb-heart">♥</span> Shan & Anusha Babe{" "}
            <span className="sb-heart">♥</span>
          </h1>
          <p className="sb-subtitle">Videos & Photos</p>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="sb-tabs">
        <button
          className={`sb-tab ${tab === "upload" ? "active" : ""}`}
          onClick={() => setTab("upload")}
        >
          📤 Upload
        </button>
        <button
          className={`sb-tab ${tab === "gallery" ? "active" : ""}`}
          onClick={() => {
            setTab("gallery");
            if (downloadUnlocked && files.length === 0) loadFiles();
          }}
        >
          📸 Gallery & Download
        </button>
      </div>

      <div className="sb-content">
        {/* ── Upload Tab ── */}
        {tab === "upload" && (
          <div className="sb-section">
            {!uploadUnlocked ? (
              <div className="sb-password-gate">
                <div className="sb-lock-icon">🔒</div>
                <h2>Enter Upload Password</h2>
                <div className="sb-pwd-row">
                  <input
                    type="password"
                    value={uploadPwd}
                    onChange={(e) => setUploadPwd(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && unlockUpload()}
                    placeholder="Password"
                    className="sb-input"
                  />
                  <button className="sb-btn sb-btn-primary" onClick={unlockUpload}>
                    Unlock
                  </button>
                </div>
                {uploadPwdError && <p className="sb-error">{uploadPwdError}</p>}
              </div>
            ) : (
              <div className="sb-upload-area">
                <div
                  className="sb-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dragover"); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove("dragover")}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove("dragover");
                    const droppedFiles = Array.from(e.dataTransfer.files).filter(
                      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
                    );
                    setSelectedFiles((prev) => [...prev, ...droppedFiles]);
                  }}
                >
                  <div className="sb-dropzone-icon">📁</div>
                  <p>Click or drag & drop photos and videos here</p>
                  <p className="sb-hint">No compression — original quality preserved</p>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  accept="image/*,video/*"
                  style={{ display: "none" }}
                />

                {/* Show upload result message even after files are cleared */}
                {selectedFiles.length === 0 && uploadMsg && (
                  <div className="sb-upload-result">
                    <p className={`sb-msg ${uploadMsg.startsWith("Error") ? "error" : "success"}`}>
                      {uploadMsg}
                    </p>
                    <button
                      className="sb-btn sb-btn-primary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload More
                    </button>
                  </div>
                )}

                {selectedFiles.length > 0 && (
                  <div className="sb-selected-files">
                    <h3>Selected ({selectedFiles.length} files)</h3>
                    <div className="sb-file-list">
                      {selectedFiles.map((f, i) => (
                        <div key={i} className="sb-file-item">
                          <span className="sb-file-icon">
                            {f.type.startsWith("video/") ? "🎬" : "🖼️"}
                          </span>
                          <span className="sb-file-name">{f.name}</span>
                          <span className="sb-file-size">{formatSize(f.size)}</span>
                          <button className="sb-btn-remove" onClick={() => removeFile(i)}>
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="sb-upload-actions">
                      <button
                        className="sb-btn sb-btn-primary sb-btn-lg"
                        onClick={handleUpload}
                        disabled={uploading}
                      >
                        {uploading
                          ? `Uploading... ${uploadProgress}%`
                          : `Upload ${selectedFiles.length} file(s)`}
                      </button>
                      {!uploading && (
                        <button
                          className="sb-btn sb-btn-ghost"
                          onClick={() => setSelectedFiles([])}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    {uploading && (
                      <div className="sb-progress-bar">
                        <div
                          className="sb-progress-fill"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}
                    {uploadMsg && (
                      <p className={`sb-msg ${uploadMsg.startsWith("Error") ? "error" : "success"}`}>
                        {uploadMsg}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Gallery & Download Tab ── */}
        {tab === "gallery" && (
          <div className="sb-section">
            {!downloadUnlocked ? (
              <div className="sb-password-gate">
                <div className="sb-lock-icon">🔒</div>
                <h2>Enter Download Password</h2>
                <div className="sb-pwd-row">
                  <input
                    type="password"
                    value={downloadPwd}
                    onChange={(e) => setDownloadPwd(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && unlockDownload()}
                    placeholder="Password"
                    className="sb-input"
                  />
                  <button className="sb-btn sb-btn-primary" onClick={unlockDownload}>
                    Unlock
                  </button>
                </div>
                {downloadPwdError && <p className="sb-error">{downloadPwdError}</p>}
              </div>
            ) : loadingFiles ? (
              <div className="sb-loading">
                <div className="sb-spinner" />
                <p>Loading files...</p>
              </div>
            ) : files.length === 0 ? (
              <div className="sb-empty">
                <p>No files uploaded yet.</p>
                <button className="sb-btn sb-btn-primary" onClick={() => setTab("upload")}>
                  Upload Now
                </button>
              </div>
            ) : (
              <div className="sb-gallery">
                <div className="sb-gallery-toolbar">
                  <span className="sb-file-count">
                    {photos.length} photo{photos.length !== 1 && "s"},{" "}
                    {videos.length} video{videos.length !== 1 && "s"}
                  </span>
                  <div className="sb-toolbar-actions">
                    <button className="sb-btn sb-btn-primary" onClick={loadFiles}>
                      🔄 Refresh
                    </button>
                    <button
                      className="sb-btn sb-btn-accent"
                      onClick={downloadAll}
                      disabled={downloadingAll}
                    >
                      {downloadingAll ? "Preparing ZIP..." : "⬇ Download All"}
                    </button>
                  </div>
                </div>

                {/* Date-wise grouped content (newest first) */}
                {dateGroups.map((group) => {
                  const gPhotos = group.files.filter((f) => !isVideo(f.name));
                  const gVideos = group.files.filter((f) => isVideo(f.name));
                  const isOpen = expandedDates.has(group.label);
                  return (
                    <div key={group.label} className="sb-date-group">
                      <button
                        className={`sb-date-header ${isOpen ? "open" : ""}`}
                        onClick={() => toggleDate(group.label)}
                      >
                        <span className="sb-date-arrow">{isOpen ? "▼" : "▶"}</span>
                        <span className="sb-date-label">📅 {group.label}</span>
                        <span className="sb-date-count">
                          {gPhotos.length > 0 && `${gPhotos.length} photo${gPhotos.length !== 1 ? "s" : ""}`}
                          {gPhotos.length > 0 && gVideos.length > 0 && ", "}
                          {gVideos.length > 0 && `${gVideos.length} video${gVideos.length !== 1 ? "s" : ""}`}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="sb-date-content">
                          {gPhotos.length > 0 && (
                            <>
                              <h3 className="sb-section-title">📸 Photos</h3>
                              <div className="sb-grid">
                                {gPhotos.map((f) => (
                                  <div key={f.key} className="sb-card">
                                    <div className="sb-thumb" onClick={() => setPreviewFile(f)}>
                                      <img src={f.url} alt={f.name} loading="lazy" />
                                    </div>
                                    <div className="sb-card-info">
                                      <span className="sb-card-name" title={f.name.replace(/^\d+-/, "")}>
                                        {f.name.replace(/^\d+-/, "")}
                                      </span>
                                      <span className="sb-card-size">{formatSize(f.size)}</span>
                                    </div>
                                    <div className="sb-card-actions">
                                      <button
                                        className="sb-btn sb-btn-sm sb-btn-primary"
                                        onClick={() => downloadFile(f)}
                                        disabled={downloadingKeys.has(f.key)}
                                      >
                                        {downloadingKeys.has(f.key) ? "..." : "⬇"}
                                      </button>
                                      <button
                                        className={`sb-btn sb-btn-sm sb-btn-share ${copiedKey === f.key ? "copied" : ""}`}
                                        onClick={() => shareFile(f)}
                                      >
                                        {copiedKey === f.key ? "✓ Copied" : "🔗"}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {gVideos.length > 0 && (
                            <>
                              <h3 className="sb-section-title">🎬 Videos</h3>
                              <div className="sb-grid sb-grid-videos">
                                {gVideos.map((f) => (
                                  <div key={f.key} className="sb-card sb-card-video">
                                    <div className="sb-thumb sb-thumb-video" onClick={() => setPreviewFile(f)}>
                                      <video src={f.url} preload="metadata" />
                                      <div className="sb-play-overlay">▶</div>
                                    </div>
                                    <div className="sb-card-info">
                                      <span className="sb-card-name" title={f.name.replace(/^\d+-/, "")}>
                                        {f.name.replace(/^\d+-/, "")}
                                      </span>
                                      <span className="sb-card-size">{formatSize(f.size)}</span>
                                    </div>
                                    <div className="sb-card-actions">
                                      <button
                                        className="sb-btn sb-btn-sm sb-btn-primary"
                                        onClick={() => downloadFile(f)}
                                        disabled={downloadingKeys.has(f.key)}
                                      >
                                        {downloadingKeys.has(f.key) ? "..." : "⬇"}
                                      </button>
                                      <button
                                        className={`sb-btn sb-btn-sm sb-btn-share ${copiedKey === f.key ? "copied" : ""}`}
                                        onClick={() => shareFile(f)}
                                      >
                                        {copiedKey === f.key ? "✓ Copied" : "🔗"}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Preview Modal */}
      {previewFile && (
        <div className="sb-modal" onClick={() => setPreviewFile(null)}>
          <div className="sb-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="sb-modal-close" onClick={() => setPreviewFile(null)}>
              ✕
            </button>
            {isVideo(previewFile.name) ? (
              <video
                src={previewFile.url}
                controls
                autoPlay
                className="sb-modal-media"
              />
            ) : (
              <img src={previewFile.url} alt="" className="sb-modal-media" />
            )}
            <div className="sb-modal-footer">
              <span>{previewFile.name.replace(/^\d+-/, "")}</span>
              <button
                className="sb-btn sb-btn-primary"
                onClick={() => downloadFile(previewFile)}
              >
                ⬇ Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
